import React, { useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, PlayIcon, PauseIcon, TimerIcon, ListChecksIcon, HandIcon, ZapIcon, RotateCcwIcon } from 'lucide-react';
import { exercises } from '../data/exercises';
import ExerciseCard from './ExerciseCard';
import ProgressBar from './ProgressBar';
import RepCounter from './RepCounter';
import Timer from './Timer';
import CelebrationEffect from './CelebrationEffect';
import PacingControl from './PacingControl';
import { getSavedPacingSpeed, savePacingSpeed } from '../utils/storage';
const WorkoutApp = () => {
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [isTimeBased, setIsTimeBased] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [reps, setReps] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(60); // Default 60 seconds per exercise in time mode
  const [isPacingMode, setIsPacingMode] = useState(false);
  const [pacingSpeed, setPacingSpeed] = useState(30); // Initial value, will be updated from storage
  const [currentPaceBeat, setCurrentPaceBeat] = useState(false);
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const currentExercise = exercises[currentExerciseIndex];
  // Load saved pacing speed when exercise changes
  useEffect(() => {
    if (currentExercise) {
      const defaultSpeed = currentExercise.defaultPacingSpeed || 30;
      const savedSpeed = getSavedPacingSpeed(currentExercise.id, defaultSpeed);
      setPacingSpeed(savedSpeed);
    }
  }, [currentExerciseIndex, currentExercise]);
  const toggleMode = () => {
    setIsTimeBased(!isTimeBased);
    setReps(0);
    setTimeRemaining(60);
    setIsActive(false);
    setIsPacingMode(false);
  };
  const togglePacingMode = () => {
    if (isTimeBased) return; // Don't allow pacing mode in time-based workouts
    setIsPacingMode(!isPacingMode);
    setIsActive(false);
  };
  const toggleActive = () => {
    setIsActive(!isActive);
  };
  const resetExercise = () => {
    setReps(0);
    setTimeRemaining(60);
    setIsActive(false);
    setShowCelebration(false);
  };
  const nextExercise = () => {
    if (currentExerciseIndex < exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      resetExercise();
    }
  };
  const prevExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
      resetExercise();
    }
  };
  const incrementRep = () => {
    if (reps < currentExercise.reps) {
      setReps(reps + 1);
      if (reps + 1 >= currentExercise.reps) {
        setIsActive(false);
        setShowCelebration(true);
        setTimeout(() => {
          setShowCelebration(false);
        }, 3000);
      }
    }
  };
  // Update pacing speed and save to storage
  const updatePacingSpeed = (speed: number) => {
    setPacingSpeed(speed);
    // Save to storage and show saved indicator
    if (currentExercise) {
      savePacingSpeed(currentExercise.id, speed);
      setShowSavedIndicator(true);
      // Hide indicator after 1.5 seconds
      setTimeout(() => {
        setShowSavedIndicator(false);
      }, 1500);
    }
  };
  // Reset pacing speed to default for current exercise
  const resetPacingSpeed = () => {
    if (currentExercise && currentExercise.defaultPacingSpeed) {
      updatePacingSpeed(currentExercise.defaultPacingSpeed);
    }
  };
  // Handle automatic pacing for reps
  useEffect(() => {
    let interval = null;
    if (isPacingMode && isActive && reps < currentExercise.reps) {
      // Convert pacingSpeed (reps per minute) to milliseconds between reps
      const intervalTime = 60 / pacingSpeed * 1000;
      interval = setInterval(() => {
        // Toggle the pace beat for visual indication
        setCurrentPaceBeat(beat => !beat);
        // Only increment on every other beat (when true)
        // This creates a visual cue before the actual increment
        if (currentPaceBeat) {
          incrementRep();
        }
      }, intervalTime / 2); // Divide by 2 because we're toggling twice per rep
    } else if (!isActive && interval) {
      clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPacingMode, isActive, reps, currentExercise.reps, pacingSpeed, currentPaceBeat]);
  // Handle time-based workouts
  useEffect(() => {
    let interval = null;
    if (isTimeBased && isActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(time => {
          if (time <= 1) {
            setIsActive(false);
            setShowCelebration(true);
            setTimeout(() => {
              setShowCelebration(false);
            }, 3000);
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    } else if (!isActive && interval) {
      clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimeBased, isActive, timeRemaining]);
  return <div className="max-w-3xl mx-auto p-4 py-8" data-id="element-57">
      <h1 className="text-3xl font-bold text-center mb-6" data-id="element-58">Workout Coach</h1>
      <div className="bg-white rounded-xl shadow-lg overflow-hidden" data-id="element-59">
        <ProgressBar current={currentExerciseIndex + 1} total={exercises.length} data-id="element-60" />
        <ExerciseCard exercise={currentExercise} data-id="element-61" />
        <div className="p-6" data-id="element-62">
          {isTimeBased ? <Timer timeRemaining={timeRemaining} isActive={isActive} data-id="element-63" /> : <>
              <RepCounter current={reps} total={currentExercise.reps} onIncrement={incrementRep} isActive={isActive && !isPacingMode} isPacing={isPacingMode && isActive} paceBeat={currentPaceBeat} data-id="element-64" />
              {isPacingMode && <PacingControl pacingSpeed={pacingSpeed} setPacingSpeed={updatePacingSpeed} isActive={isActive} onReset={resetPacingSpeed} showSaved={showSavedIndicator} exerciseName={currentExercise.name} isDefault={pacingSpeed === currentExercise.defaultPacingSpeed} data-id="element-65" />}
            </>}
          <div className="flex justify-between items-center mt-8" data-id="element-66">
            <button onClick={prevExercise} disabled={currentExerciseIndex === 0} className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50" aria-label="Previous exercise" data-id="element-67">
              <ChevronLeftIcon size={24} data-id="element-68" />
            </button>
            <button onClick={toggleActive} className={`px-8 py-3 rounded-full font-medium ${isActive ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`} data-id="element-69">
              {isActive ? <span className="flex items-center" data-id="element-70">
                  <PauseIcon size={20} className="mr-2" data-id="element-71" /> Pause
                </span> : <span className="flex items-center" data-id="element-72">
                  <PlayIcon size={20} className="mr-2" data-id="element-73" />{' '}
                  {reps > 0 || timeRemaining < 60 ? 'Resume' : 'Start'}
                </span>}
            </button>
            <button onClick={nextExercise} disabled={currentExerciseIndex === exercises.length - 1} className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50" aria-label="Next exercise" data-id="element-74">
              <ChevronRightIcon size={24} data-id="element-75" />
            </button>
          </div>
          <div className="flex gap-2 mt-4" data-id="element-76">
            <button onClick={toggleMode} className="flex-1 py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center" data-id="element-77">
              {isTimeBased ? <>
                  <ListChecksIcon size={18} className="mr-2" data-id="element-78" /> Switch to
                  Rep-Based
                </> : <>
                  <TimerIcon size={18} className="mr-2" data-id="element-79" /> Switch to Time-Based
                </>}
            </button>
            {!isTimeBased && <button onClick={togglePacingMode} className={`flex-1 py-2 px-4 rounded-lg flex items-center justify-center ${isPacingMode ? 'bg-purple-600 hover:bg-purple-700 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-800'}`} data-id="element-80">
                {isPacingMode ? <>
                    <HandIcon size={18} className="mr-2" data-id="element-81" /> Manual Mode
                  </> : <>
                    <ZapIcon size={18} className="mr-2" data-id="element-82" /> Pacing Mode
                  </>}
              </button>}
          </div>
        </div>
      </div>
      {showCelebration && <CelebrationEffect data-id="element-83" />}
    </div>;
};
export default WorkoutApp;